/* LEVEL 2 — 急診：腹痛的少女（ruptured ectopic pregnancy masquerading as appendicitis） */
import {
  ask,
  C,
  type Cell,
  chat,
  type ChoiceItem,
  clearWait,
  clock,
  closeDialog,
  closeZoom,
  type Ctx,
  doc,
  DRAW,
  drawEcg,
  ent,
  eventBanner,
  finding,
  findEnt,
  incomingCall,
  type Level,
  leaveBy,
  me,
  mkEnt,
  note,
  obox,
  openDialog,
  openMonitor,
  openZoom,
  pipSet,
  playEnding,
  post,
  ptext,
  R,
  rebuildDyn,
  session,
  setZBar,
  spawnAt,
  spend,
  strList,
  T,
  toast,
  tr,
  walkEnt,
  type ZoomCfg,
} from "../../engine";
import { isTripUrgency, startVsTrip, type TripUrgency, type VsTrip } from "../shared/vs-trip";
import { type BedAnim, bedAnimPos, bedHots, drawBedScene } from "../shared/bedside";

const FACTS = {
  onset: tr(
    "今天下午三點左右開始痛，一開始右下腹悶痛，傍晚變成刺痛，現在整個下腹都痛",
    "The pain started around 3 p.m. today: a dull ache in the right lower belly at first, sharp by evening, and now the whole lower belly hurts",
  ),
  dizzy: tr(
    "站起來會頭暈、眼前發黑，晚上在家上廁所差點昏倒",
    "Gets dizzy and her vision goes dark when she stands; nearly fainted in the bathroom at home this evening",
  ),
  shoulder: tr("右肩膀有點痠痛", "Her right shoulder aches a little"),
  gi: tr("有點噁心沒有吐，沒有拉肚子，沒有發燒", "A little nauseous but hasn't vomited; no diarrhea, no fever"),
  urine: tr("小便正常", "Peeing normally"),
  last_meal: tr("晚上六點吃了一碗麵", "Had a bowl of noodles at 6 p.m."),
  pmh: tr("沒有慢性病、沒開過刀、沒有過敏、沒吃藥", "No chronic illnesses, no surgeries, no allergies, no medications"),
  spotting: tr(
    "「上禮拜的月經」其實只是三天的咖啡色少量出血，比平常少很多",
    '"Last week\'s period" was really just three days of light brown spotting, much less than usual',
  ),
  real_lmp: tr(
    "前一次正常月經大概八月中，平常約 30 天一次",
    "Her last normal period was around mid-August; she's usually every 30 days or so",
  ),
  breast: tr("最近胸部有點脹痛", "Her breasts have felt a bit swollen and sore lately"),
  sexual: tr("有交往半年的男朋友，有性行為", "Has had a boyfriend for six months; sexually active"),
  contraception: tr(
    "大多用保險套，九月初破掉一次，沒吃事後藥",
    "Mostly uses condoms; one broke in early September, and she didn't take emergency contraception",
  ),
  pid: tr(
    "去年因為分泌物異常、下腹痛吃過一週抗生素",
    "Took a week of antibiotics last year for abnormal discharge and lower belly pain",
  ),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

/** Order id → [label, minutes until ready]. */
const ORD = {
  upt: [tr("尿液驗孕（UPT）", "Urine pregnancy test (UPT)"), 10],
  bhcg: [tr("血清 β-hCG", "Serum β-hCG"), 50],
  cbc: ["CBC", 25],
  ts: [tr("血型與抗體篩檢（備血）", "Type and screen (blood prep)"), 40],
  coag: [tr("PT／aPTT", "PT/aPTT"), 30],
  ua: [tr("尿液常規", "UA"), 20],
  tvs: [tr("陰道超音波（請 R2 小周）", "Transvaginal US (Chou, R2)"), 20],
  ct: [tr("腹部 CT", "CT abdomen"), 45],
  iv: [tr("兩條 18G 留置針", "Two 18G IVs"), 5],
  fluid: [tr("LR 500 mL 快速輸注", "LR 500 mL bolus"), 5],
  npo: [tr("禁食禁水", "NPO"), 1],
  analgesic: [tr("靜脈止痛", "IV pain relief"), 5],
} as const satisfies Record<string, readonly [string, number]>;
type OrderId = keyof typeof ORD;
const isOrder = (x: string): x is OrderId => x in ORD;

interface Vitals {
  sbp: number;
  dbp: number;
  hr: number;
  rr: number;
  spo2: number;
}
interface Order {
  at: number;
  ready: number;
  done: boolean;
}
type PtLoc = "bed" | "ct" | "wc";

interface Vars {
  revealed: Set<FactId>;
  orders: Partial<Record<OrderId, Order>>;
  results: { id: OrderId; t: number; text: string }[];
  vitalsSeen: { t: number; v: Vitals }[];
  motherAway: number | null;
  motherAwayEver: boolean;
  ptLoc: PtLoc;
  collapsed: boolean;
  collapseAt: number | null;
  fluidAt: number | null;
  hcgKnownAt: number | null;
  ctWarned: boolean;
  /** Game minute the porter takes her to CT. */
  ctLeave: number | null;
  vsTalked: boolean;
  vsAccepted: boolean;
  vsArriveAt: number | null;
  vsHere: boolean;
  trip: VsTrip | null;
  bedAnim: BedAnim | null;
  bedGone: boolean;
  toiletAt: number | null;
  r2State: "none" | "coming" | "here";
  /** Game minute 林主任 calls in after a collapse he wasn't told about. */
  callAt: number | null;
  endAt: number | null;
}

const S = session<Vars>();
const DOOR: Cell = [15, 11];

/* ---------- patient state ---------- */

const threshold = (): number => 45 + (S.fluidAt !== null && S.fluidAt < 45 ? 8 : 0);

function vitals(): Vitals {
  const t = S.t;
  let sbp: number;
  let dbp: number;
  let hr: number;
  let rr: number;
  let spo2 = 99;
  if (S.collapsed) {
    const dt = t - (S.collapseAt ?? t);
    sbp = 78 + (S.fluidAt !== null ? Math.min(dt, 15) * 0.5 : 0);
    dbp = 45;
    hr = 132;
    rr = 24;
    spo2 = 96;
  } else {
    sbp = 106 - 0.42 * t;
    dbp = 68 - 0.3 * t;
    hr = 98 + 0.6 * t;
    rr = 18 + (t > 30 ? 2 : 0);
    if (S.fluidAt !== null && t - S.fluidAt < 25) {
      sbp += 7;
      dbp += 4;
      hr -= 6;
    }
  }
  return { sbp: Math.round(sbp), dbp: Math.round(dbp), hr: Math.round(hr), rr, spo2 };
}

const vtxt = (v: Vitals): string =>
  tr(
    `BP ${v.sbp}/${v.dbp}，HR ${v.hr}，RR ${v.rr}，SpO₂ ${v.spo2}%`,
    `BP ${v.sbp}/${v.dbp}, HR ${v.hr}, RR ${v.rr}, SpO₂ ${v.spo2}%`,
  );

function condition(): string {
  if (S.collapsed)
    return tr(
      "剛剛昏倒過，意識模糊，只能很小聲很簡短地回應",
      "just passed out; confused, can only answer very quietly and in a few words",
    );
  if (S.t < 18) return tr("腹痛 6/10，怕痛也怕被罵", "belly pain 6/10; scared of the pain and of getting scolded");
  if (S.t < 34) return tr("腹痛 7/10，越來越暈，臉色蒼白", "belly pain 7/10, getting dizzier, looks pale");
  return tr("非常暈、想吐、冒冷汗，講話很虛弱", "very dizzy, nauseous, in a cold sweat, voice very weak");
}

function momHere(): boolean {
  const m = findEnt("mother");
  return !!m && !m.hidden && S.motherAway === null;
}

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `量過的生命徵象：${S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("；") || "沒有自己看過（分診 BP 108/70，HR 96）"}`,
      `Vitals checked: ${S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("; ") || "hasn't checked any personally (triage BP 108/70, HR 96)"}`,
    ),
  );
  out.push(
    tr(
      `檢查與結果：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Exams and results: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  const pend = (Object.entries(S.orders) as [OrderId, Order][]).filter(([, o]) => !o.done).map(([id]) => ORD[id][0]);
  out.push(tr(`已開未出：${pend.join("、") || "無"}`, `Ordered, still pending: ${pend.join(", ") || "none"}`));
  out.push(
    tr(
      `問出：${[...S.revealed].map((id) => FACTS[id]).join("；") || "幾乎沒問出什麼"}`,
      `History obtained: ${[...S.revealed].map((id) => FACTS[id]).join("; ") || "almost nothing"}`,
    ),
  );
  out.push(
    tr(
      `陳媽媽曾離開床邊：${S.motherAwayEver ? "是" : "否"}`,
      `Mrs. Chen has stepped away from the bedside at some point: ${S.motherAwayEver ? "yes" : "no"}`,
    ),
  );
  return out.join("\n");
}

/* ---------- 林主任 on the way ---------- */

/* ---------- monitor & exam ---------- */

function openMon(): void {
  const v = vitals();
  S.vitalsSeen.push({ t: S.t, v });
  post("bed", "result", null, vtxt(v), { silent: true });
  openMonitor(
    tr("7 床・監視器", "Bed 7 · Monitor"),
    () => {
      const now = vitals();
      const last = S.vitalsSeen.at(-1) ?? { t: S.t, v: now };
      return {
        hr: now.hr,
        spo2: now.spo2,
        rr: now.rr,
        sbp: last.v.sbp,
        dbp: last.v.dbp,
        nibpT: clock(last.t),
        alarm: now.hr > 115,
      };
    },
    [
      C(tr("重新量 NIBP（1 分鐘）", "Recheck NIBP (1 min)"), () => {
        const nv = vitals();
        S.vitalsSeen.push({ t: S.t, v: nv });
        post("bed", "result", null, vtxt(nv));
        spend(1);
      }),
    ],
    () => {
      if (S.dialog !== "bed") return;
      const z = bedZoom();
      if (z) openZoom({ dlg: "bed", ...z });
    },
  );
  spend(1);
}

function exam(k: "look" | "abd" | "pelvic"): void {
  if (S.ptLoc !== "bed") {
    note(tr("病人不在床上。", "The patient isn't in bed."), "bed");
    return;
  }
  if (k === "look") {
    me("bed", tr("（觀察病人）", "(Looks her over)"));
    post(
      "bed",
      "narr",
      null,
      S.collapsed
        ? tr(
            "曉婷叫了才勉強睜眼，臉色慘白，四肢濕冷。",
            "Hsiao-Ting barely opens her eyes when you call her name. She is ashen, her arms and legs cold and clammy.",
          )
        : S.t < 18
          ? tr(
              "曉婷側躺蜷著身體，一手壓著下腹，臉色偏白，額頭有點汗。",
              "Hsiao-Ting lies curled on her side, one hand pressed to her lower belly. She looks pale, with a little sweat on her forehead.",
            )
          : S.t < 34
            ? tr(
                "曉婷臉色明顯比剛才白，嘴唇沒什麼血色。",
                "Hsiao-Ting is clearly paler than before; her lips have little color.",
              )
            : tr(
                "曉婷冒冷汗，一動就皺眉，講話有氣無力。",
                "Hsiao-Ting is in a cold sweat, winces whenever she moves, and her voice is faint.",
              ),
    );
    spend(1);
    return;
  }
  if (k === "abd") {
    me("bed", tr("（腹部檢查）", "(Abdominal exam)"));
    const t = tr(
      "腹部稍脹，下腹整片壓痛，右下腹最明顯，有反彈痛和輕微肌肉防禦；最痛點比 McBurney 點更偏骨盆。",
      "Abdomen mildly distended. Tender across the whole lower abdomen, worst in the RLQ, with rebound and mild guarding; the point of maximal tenderness is lower, toward the pelvis, than McBurney's point.",
    );
    finding(tr("腹部", "Abdomen"), t);
    post("bed", "narr", null, t);
    spend(3);
    return;
  }
  me("bed", tr("（骨盆內診）", "(Pelvic exam)"));
  const t =
    (momHere()
      ? tr(
          "阿芬拉上簾子陪同，陳媽媽站在簾子外。",
          "Fen draws the curtain and chaperones; Mrs. Chen waits outside the curtain. ",
        )
      : tr("阿芬拉上簾子陪同。", "Fen draws the curtain and chaperones. ")) +
    tr(
      "陰道內少量暗紅色血液，子宮頸口閉合；子宮頸搖擺痛陽性，右側附屬器壓痛明顯、有飽滿感，後穹窿飽滿。",
      "A small amount of dark red blood in the vagina, cervical os closed. Positive cervical motion tenderness, marked right adnexal tenderness with fullness, posterior fornix full.",
    );
  finding(tr("骨盆內診", "Pelvic exam"), t);
  post("bed", "narr", null, t);
  spend(6);
}

/* ---------- orders ---------- */

function order(id: OrderId): void {
  if (S.orders[id]) return;
  if (id === "ct" && S.hcgKnownAt === null && !S.ctWarned) {
    S.ctWarned = true;
    ask(
      "nurse",
      "afen",
      tr(
        "醫師，她 19 歲耶，CT 前不用先驗孕喔？你確定要直接排的話，再開一次給我。",
        "Doctor, she's 19. No pregnancy test before CT? If you're sure you want it straight away, order it again.",
      ),
    );
    return;
  }
  if (id === "fluid" && !S.orders.iv) order("iv");
  S.orders[id] = { at: S.t, ready: S.t + ORD[id][1], done: false };
  if (id === "ct" && S.ptLoc === "bed" && !S.collapsed) S.ctLeave = S.t + 8;
  if (id === "tvs") S.r2State = "coming";
}

const PROCEDURE_REPLY: Partial<Record<OrderId, string>> = tr(
  {
    iv: "兩條 18G 打好了。",
    fluid: "LR 500 全速。",
    npo: "跟病人說了，不能吃不能喝。",
    analgesic: "止痛藥打了。",
  },
  {
    iv: "Two 18Gs are in.",
    fluid: "LR 500, wide open.",
    npo: "Told her: nothing to eat or drink.",
    analgesic: "Pain med's in.",
  },
);

function deliver(id: OrderId): void {
  const o = S.orders[id];
  if (!o) return;
  o.done = true;
  const reply = PROCEDURE_REPLY[id];
  if (reply) {
    if (id === "fluid") S.fluidAt = S.t;
    post("nurse", "npc", "afen", reply);
    return;
  }
  const results: Partial<Record<OrderId, string>> = tr(
    {
      upt: "尿液驗孕（UPT）：陽性 (+)",
      bhcg: "血清 β-hCG：2,850 mIU/mL",
      cbc: `CBC：WBC 11,200，Hb ${(11.4 - 0.035 * o.at).toFixed(1)} g/dL，PLT 245k`,
      ts: "血型 O 型 Rh(+)，抗體篩檢陰性，可以備血",
      coag: "PT 11.2 秒，aPTT 29 秒",
      ua: "尿液常規：WBC 0–2，RBC 3–5，nitrite (−)",
      ct: "腹部 CT：腹腔中量高密度游離液體（疑似出血），右側附屬器腫塊；闌尾正常",
      tvs: "陰道超音波：子宮腔內沒有妊娠囊；右側附屬器約 3.2 cm 複雜性腫塊；後穹窿中到大量帶回音的游離液體",
    },
    {
      upt: "Urine pregnancy test (UPT): positive (+)",
      bhcg: "Serum β-hCG: 2,850 mIU/mL",
      cbc: `CBC: WBC 11,200, Hb ${(11.4 - 0.035 * o.at).toFixed(1)} g/dL, PLT 245k`,
      ts: "Type O Rh(+), antibody screen negative; blood can be crossmatched",
      coag: "PT 11.2 s, aPTT 29 s",
      ua: "UA: WBC 0–2, RBC 3–5, nitrite (−)",
      ct: "CT abdomen: moderate high-density free fluid in the abdomen (suspicious for blood), right adnexal mass; appendix normal",
      tvs: "Transvaginal US: no intrauterine gestational sac; complex right adnexal mass about 3.2 cm; moderate to large echogenic free fluid in the posterior cul-de-sac",
    },
  );
  const text = results[id] ?? "";
  S.results.push({ id, t: S.t, text });
  finding(tr("結果", "Result"), text);
  if ((id === "upt" || id === "bhcg") && S.hcgKnownAt === null) S.hcgKnownAt = S.t;
  if (id === "tvs") {
    post("bed", "result", "r2", text);
    post(
      "bed",
      "npc",
      "r2",
      tr(
        "我先回產房，那邊有一個要生。主任那邊你自己報告，練習一下。",
        "I'm heading back to L&D, someone's about to deliver. Present to the attending yourself. Good practice.",
      ),
    );
    leaveBy(ent("r2"), DOOR);
  } else post("nurse", "result", "afen", text);
}

/* ---------- mother & patient movement ---------- */

function momLeave(): void {
  if (!momHere()) return;
  S.motherAway = S.t;
  const m = ent("mother");
  post(
    "bed",
    "narr",
    null,
    tr(
      "陳媽媽拿起包包，走到外面的候診椅坐下。",
      "Mrs. Chen picks up her bag, walks out, and sits down in the waiting area.",
    ),
  );
  walkEnt(m, [14, 8]);
  m.onArrive = () => {
    m.face = "down";
    m.act = "mom";
    rebuildDyn();
  };
}

function momBack(noteText?: string): void {
  if (S.motherAway === null) return;
  S.motherAway = null;
  const m = ent("mother");
  m.act = "bed";
  walkEnt(m, [4, 3]);
  m.onArrive = () => {
    m.face = "left";
    rebuildDyn();
  };
  if (noteText) post("bed", "narr", null, noteText);
}

function toilet(): void {
  if (S.ptLoc !== "bed" || S.collapsed) return;
  S.ptLoc = "wc";
  S.toiletAt = S.t;
  clearWait("toilet");
  post(
    "bed",
    "narr",
    null,
    tr(
      "曉婷扶著床欄慢慢下床，往廁所走。",
      "Hsiao-Ting grips the bed rail, eases herself out of bed, and heads for the restroom.",
    ),
  );
  closeZoom();
}

function collapse(): void {
  S.collapsed = true;
  S.collapseAt = S.t;
  const where = S.ptLoc;
  if (where === "ct")
    post(
      "bed",
      "narr",
      null,
      tr(
        "CT 室傳來廣播。曉婷在檢查台上叫不醒，被緊急推回急診。",
        "An overhead page from CT: Hsiao-Ting won't wake up on the scanner table. She is rushed back to the ER.",
      ),
    );
  else if (where === "wc")
    post(
      "bed",
      "narr",
      null,
      tr(
        "廁所傳來「碰」的一聲。阿芬衝進去，曉婷倒在地上。大家把她抬回床上。",
        "A thud from the restroom. Fen rushes in: Hsiao-Ting is on the floor. Together they lift her back into bed.",
      ),
    );
  else
    post(
      "bed",
      "narr",
      null,
      tr(
        "曉婷說想坐起來，才撐起身體眼睛就一翻，倒回床上。",
        "Hsiao-Ting says she wants to sit up. The moment she pushes herself up, her eyes roll back and she slumps onto the bed.",
      ),
    );
  S.ptLoc = "bed";
  momBack(tr("陳媽媽尖叫著跑回床邊。", "Mrs. Chen runs back to the bedside, screaming."));
  eventBanner(tr("病人昏倒", "Patient collapsed"), tr(`BP 78/45　HR 132`, `BP 78/45 · HR 132`), true);
  post(
    "nurse",
    "alert",
    "afen",
    tr(
      "醫師！7 床昏倒了！BP 78/45，HR 132，我們推急救區、打上 IV 了！",
      "Doctor! Bed 7 collapsed! BP 78/45, HR 132. We've moved her to resus and got an IV in!",
    ),
  );
  if (S.vsAccepted) {
    if (S.vsArriveAt !== null && S.vsArriveAt - S.t > 5) {
      S.vsArriveAt = S.t + 5;
      startVsTrip("high", 5);
    }
    post("nurse", "npc", "afen", tr("我打給主任了，他說馬上到。", "I called the attending. He says he's on his way."));
  } else S.callAt = S.t + 2;
}

/* ---------- endings ---------- */

function pushOut(): void {
  S.bedAnim = {
    pts: [
      [3, 2],
      [3, 5],
      [11, 5],
      [15, 5],
      [15, 10],
    ],
    t0: performance.now(),
  };
}

function endA(): void {
  void playEnding(
    "A",
    tr("及時進刀房", "To the OR in Time"),
    [
      {
        who: "vs",
        text: tr(
          "子宮外孕破裂。通知開刀房，備血，我刀房見。",
          "Ruptured ectopic. Call the OR, get blood ready. I'll see you in the OR.",
        ),
      },
      {
        fn: () => {
          pushOut();
          leaveBy(ent("vs"), DOOR);
          leaveBy(ent("mother"), DOOR);
        },
        text: tr(
          "阿芬拉起床欄，曉婷被推往手術室。陳媽媽一路跟著。",
          "Fen raises the bed rails, and Hsiao-Ting is wheeled off to the OR. Mrs. Chen follows the whole way.",
        ),
        wait: 3200,
      },
      { who: "afen", text: tr("你剛剛那通電話打得不錯。", "That was a good phone call just now.") },
      { text: tr("（待續：第五關　刀房）", "(To be continued: Level 5 · OR)") },
    ],
    () =>
      tr(
        `驗孕在 ${S.hcgKnownAt !== null ? clock(S.hcgKnownAt) : "—"} 出結果，林主任在病人昏倒前接手。`,
        `The pregnancy test came back at ${S.hcgKnownAt !== null ? clock(S.hcgKnownAt) : "—"}, and Dr. Lin took over before she collapsed.`,
      ),
  );
}

function endB(): void {
  void playEnding(
    "B",
    tr("急救之後", "After the Crash"),
    [
      {
        who: "vs",
        text: tr(
          "推開刀房。輸血、兩條大號 IV。為什麼現在才打給我？",
          "Take her to the OR. Transfuse, two large-bore IVs. Why are you only calling me now?",
        ),
      },
      {
        fn: () => {
          pushOut();
          leaveBy(ent("vs"), DOOR);
          leaveBy(ent("mother"), DOOR);
        },
        text: tr(
          "曉婷被推往手術室。陳媽媽邊哭邊跟著。",
          "Hsiao-Ting is wheeled off to the OR. Mrs. Chen follows, crying.",
        ),
        wait: 3200,
      },
      { text: tr("（待續：第五關　刀房）", "(To be continued: Level 5 · OR)") },
    ],
    () =>
      tr(
        `曉婷在 ${clock(S.collapseAt ?? S.t)} 昏倒。驗孕：${S.hcgKnownAt !== null ? clock(S.hcgKnownAt) : "沒驗"}。`,
        `Hsiao-Ting collapsed at ${clock(S.collapseAt ?? S.t)}. Pregnancy test: ${S.hcgKnownAt !== null ? clock(S.hcgKnownAt) : "never done"}.`,
      ),
  );
}

/* ---------- close-up ---------- */

function bedZoom(): ZoomCfg | null {
  if (S.ptLoc !== "bed") return null;
  return {
    title: tr("7 床", "Bed 7"),
    sub: tr("點監視器、肚子、骨盆……", "Tap the monitor, belly, pelvis…"),
    draw: (c: Ctx, now: number) => {
      const v = vitals();
      drawBedScene(c, now, {
        wall: "#dfe8ee",
        wall2: "#d0dbe3",
        base: "#a9b8c4",
        floor: "#c9d6df",
        floor2: "#bac8d2",
        pt: "xiaoting_er",
        belly: false,
        blanket: "#e8e8f0",
        blanket2: "#c9ccd8",
        pale: S.collapsed || S.t > 34 ? { s: "#ece4e0", S: "#d6ccc8" } : S.t > 18 ? { s: "#f2e2d8" } : {},
        shiver: S.t > 34 || S.collapsed,
        iv: !!S.orders.iv,
        iv2: S.fluidAt !== null ? "#e6f2f8" : undefined,
        companion: momHere() ? "mother" : undefined,
        hand: false,
        monitor: (mc, mnow) => {
          drawEcg(mc, 10, 15, 26, 6, v.hr, mnow, "#3be37a");
          ptext(mc, v.hr, 37, 9, "#3be37a");
          ptext(mc, v.spo2, 37, 21, "#4fd6ff");
          if (v.hr > 115 && Math.floor(mnow / 400) % 2) obox(mc, "#ff3b4e", 47, 7, 3, 3);
        },
      });
    },
    hot: () =>
      bedHots({
        onMonitor: openMon,
        ptLabel: tr("曉婷（跟她說話）", "Hsiao-Ting (talk to her)"),
        onBelly: () =>
          setZBar(
            [
              C(tr("腹部檢查", "Abdominal exam"), () => exam("abd")),
              C(tr("觀察病人", "Look her over"), () => exam("look")),
            ],
            tr("肚子", "Belly"),
          ),
        onPad: () =>
          setZBar([C(tr("骨盆內診（6 分鐘）", "Pelvic exam (6 min)"), () => exam("pelvic"))], tr("骨盆", "Pelvis")),
        padLabel: tr("骨盆", "Pelvis"),
        onIv: () =>
          note(
            S.orders.iv
              ? tr("兩條 18G", "Two 18G IVs") + (S.fluidAt !== null ? tr("，LR 全速", ", LR wide open") : "")
              : tr("還沒打 IV。", "No IV yet."),
            "bed",
          ),
        ...(momHere() ? { companion: "mother" } : {}),
        companionLabel: tr("陳媽媽（跟她說話）", "Mrs. Chen (talk to her)"),
      }),
  };
}

/* ---------- 林主任 ---------- */

async function vsTalk(text: string, incoming?: unknown): Promise<void> {
  S.vsTalked = true;
  const v = vitals();
  const latest =
    incoming === "collapse"
      ? tr(
          "（這通是你主動打給 clerk：護理師剛通知你 7 床病人昏倒、血壓掉到 80 上下。開口先直接問 clerk 病人到底怎麼了、他評估了什麼。action 用 continue。）",
          "(You are the one calling the clerk: the nurse just told you the patient in bed 7 collapsed and her BP has dropped to around 80. Open by asking the clerk directly what is going on with the patient and what they have assessed. Use action continue.)",
        )
      : incoming === "cb"
        ? tr(
            "（你在開車趕來的路上打給 clerk，問現在血壓、心跳、輸液、備血。開口直接問。action 用 continue。）",
            "(You are driving in and call the clerk to ask about her current BP, heart rate, fluids, and blood prep. Open by asking directly. Use action continue.)",
          )
        : undefined;
  await chat(
    text,
    {
      ch: "vs",
      think: "vs",
      tier: "default",
      speakers: { vs: tr("林主任", "Dr. Lin") },
      body: tr(
        `你在醫學教育遊戲中扮演婦產科主治「林主任」，${S.trip ? "正在趕來醫院的路上" : "週五晚上在家"}，接值班 clerk 的電話。話少，用問題逼學生思考，中英夾雜（「β-hCG 多少？」「vital 呢？」「所以你的 impression 是？」）。不直接說出診斷。
【真相】19 歲林曉婷，右側子宮外孕破裂、腹內出血，生命徵象逐漸惡化。正確方向：驗孕陽性 → 超音波無子宮內孕囊、有腹水 → 評估血行動力學 → 兩條大號 IV、輸液、備血、禁食 → 緊急手術。沒排除懷孕就做 CT 是錯的。
【clerk 實際知道的】（講出以外的數據就追問「哪裡看到的？」）
${known()}
【現況】${clock()}；實際 ${vtxt(v)}${S.collapsed ? "；病人已經昏倒過" : ""}。
【action】accept_plan：clerk 說出驗孕陽性並提出子宮外孕（或破裂、腹內出血）的判斷，也注意到病人不穩。你說你現在過去，交代一兩件事。hang_up：還沒驗孕就來報告，叫他驗完再打來。continue：其他。${S.trip ? "你已經在路上，用 continue。" : ""}
【urgency】只在 accept_plan 時填：依 clerk 在電話裡傳達出來的急迫與清楚程度（不是真實狀況）。high：講出休克徵象或具體數字並請你立刻來；medium：有重點但不夠具體；low：含糊、沒有數字。`,
        `In a medical education game you play "Dr. Lin", an OB/GYN attending, ${S.trip ? "on the way in to the hospital" : "at home on a Friday night"}, taking a call from the on-call clerk. Few words; he makes the student think by asking questions, in clipped clinical shorthand ("β-hCG?" "Vitals?" "So what's your impression?"). Never state the diagnosis outright.
[TRUTH] Lin Hsiao-Ting, 19: ruptured right ectopic pregnancy with intra-abdominal bleeding; her vitals are steadily getting worse. The right path: positive pregnancy test → ultrasound shows no intrauterine sac and free fluid → assess hemodynamics → two large-bore IVs, fluids, blood prepared, NPO → emergency surgery. Sending her to CT without ruling out pregnancy is wrong.
[WHAT THE CLERK ACTUALLY KNOWS] (if they cite any numbers beyond this, press them: "Where did you see that?")
${known()}
[CURRENT STATUS] ${clock()}; actual ${vtxt(v)}${S.collapsed ? "; the patient has already collapsed once" : ""}.
[action] accept_plan: the clerk reports a positive pregnancy test, proposes ectopic pregnancy (or rupture, intra-abdominal bleeding), and has also noticed the patient is unstable. You say you're coming in now and give one or two instructions. hang_up: they are calling before any pregnancy test; tell them to call back once it's done. continue: anything else.${S.trip ? " You're already on the way: use continue." : ""}
[urgency] Fill this in only with accept_plan, based on how urgent and clear the clerk sounded on the phone (not the actual situation). high: described signs of shock or gave concrete numbers and asked you to come right away; medium: hit the key points but wasn't specific enough; low: vague, no numbers.`,
      ),
      extra: `"action":"continue","urgency":"medium"`,
      after(r) {
        if (r.action === "hang_up") post("vs", "narr", null, tr("林主任掛斷了電話。", "Dr. Lin hangs up on you."));
        if (r.action === "accept_plan" && !S.vsAccepted) {
          if (S.hcgKnownAt === null) {
            post(
              "vs",
              "npc",
              "vs",
              tr(
                "驗孕結果呢？沒有結果你怎麼講子宮外孕？",
                "Where's the pregnancy test? Without a result, how can you call it an ectopic?",
              ),
            );
            return;
          }
          const urg: TripUrgency = isTripUrgency(r.urgency) ? r.urgency : "medium";
          const eta = { high: 6, medium: 11, low: 18 }[urg];
          S.vsAccepted = true;
          S.vsArriveAt = S.t + eta;
          startVsTrip(urg, eta);
          post("vs", "narr", null, tr("林主任掛了電話。", "Dr. Lin hangs up."));
        }
      },
      cost: incoming ? 0 : 2,
    },
    latest,
  );
}

/* ---------- dialogs ---------- */

function nurseChoices(): ChoiceItem[] {
  const o = S.orders;
  const btn = (id: OrderId) =>
    C(
      ORD[id][0],
      () => {
        me("nurse", tr(`（${ORD[id][0]}）`, `(${ORD[id][0]})`));
        order(id);
        spend(1);
      },
      !!o[id],
    );
  return [
    tr("檢驗", "Labs"),
    ...(["upt", "bhcg", "cbc", "ts", "coag", "ua"] as const).map(btn),
    tr("影像", "Imaging"),
    ...(["tvs", "ct"] as const).map(btn),
    tr("處置", "Treatment"),
    ...(["iv", "fluid", "npo", "analgesic"] as const).map(btn),
  ];
}

const PT_LOC_NAME: Record<PtLoc, string> = tr(
  { bed: "7 床", ct: "CT 室", wc: "廁所" },
  { bed: "Bed 7", ct: "CT", wc: "Restroom" },
);

const dialogs: Level["dialogs"] = {
  bed: {
    title: tr("7 床", "Bed 7"),
    sub: () =>
      momHere()
        ? tr("曉婷和陳媽媽", "Hsiao-Ting and Mrs. Chen")
        : tr("曉婷（媽媽在外面）", "Hsiao-Ting (her mom is outside)"),
    pics: () => (momHere() ? ["xiaoting_er", "mother"] : ["xiaoting_er"]),
    ch: "bed",
    talk: () => S.ptLoc === "bed",
    ph: tr("對曉婷說…", "Say something to Hsiao-Ting…"),
    choices() {
      if (S.ptLoc !== "bed")
        return [
          S.ptLoc === "ct"
            ? tr("曉婷在 CT 室", "Hsiao-Ting is in CT")
            : tr("曉婷在廁所", "Hsiao-Ting is in the restroom"),
        ];
      return [
        C(tr("床邊特寫", "Bedside close-up"), () => {
          const z = bedZoom();
          if (z) openZoom({ dlg: "bed", ...z });
        }),
        C(tr("看監視器", "Check monitor"), openMon),
        C(tr("觀察病人", "Look her over"), () => exam("look")),
        C(tr("腹部檢查", "Abdominal exam"), () => exam("abd")),
        C(tr("骨盆內診", "Pelvic exam"), () => exam("pelvic")),
      ];
    },
    async onTalk(text) {
      const here = momHere();
      const sp: Record<string, string> = { xiaoting_er: tr("曉婷", "Hsiao-Ting") };
      if (here) sp.mother = tr("陳媽媽", "Mrs. Chen");
      await chat(text, {
        ch: "bed",
        think: "xiaoting_er",
        speakers: sp,
        body: tr(
          `你在醫學教育遊戲中扮演急診 7 床的病人「林曉婷」${here ? "和她的母親「陳媽媽」" : ""}。玩家是婦產科見習醫學生（clerk）。
曉婷：19 歲大一，內向有禮貌，怕痛也怕被罵。陳媽媽：52 歲，保護欲強、愛搶答，認定是盲腸炎，完全不知道女兒有男朋友。
【隱藏真相，不能說出】右側子宮外孕破裂、腹內出血中；曉婷不知道自己懷孕。
【可揭露的事實】被問到才說，括號是 id：
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
特別規則：sexual、contraception、pid 三項，陳媽媽在場時曉婷一律否認或含糊，陳媽媽會搶著說「她沒有交男朋友」；只有陳媽媽不在、clerk 態度尊重或說明保密時，曉婷才吞吞吐吐說實話。問月經她先說「上禮拜才來」，追問量、顏色才說 spotting，問前一次正常的才說 real_lmp。
【行動】act 只能是 none、mother_leave、mother_return、toilet。clerk 有禮貌地請陳媽媽先到外面等時用 mother_leave（她會真的走出去；沒理由或口氣差時媽媽可以先問為什麼，不一定走）；請她回來用 mother_return；clerk 同意曉婷自己去廁所時用 toilet。
【狀態】${clock()}；陳媽媽${here ? "在床邊" : "在外面候診椅"}；曉婷：${condition()}。已揭露：${[...S.revealed].join(",") || "無"}`,
          `In a medical education game you play the patient in ER bed 7, "Lin Hsiao-Ting"${here ? ', and her mother, "Mrs. Chen"' : ""}. The player is an OB/GYN clerk (a medical student on clerkship).
Hsiao-Ting: 19, college freshman, introverted and polite, scared of the pain and of getting scolded. Mrs. Chen: 52, very protective, always jumps in to answer for her, is convinced it's appendicitis, and has no idea her daughter has a boyfriend.
[HIDDEN TRUTH, never say it] Ruptured right ectopic pregnancy, still bleeding into the abdomen; Hsiao-Ting doesn't know she is pregnant.
[REVEALABLE FACTS] Say each one only when asked; the id is in parentheses:
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
Special rules: for sexual, contraception and pid, while Mrs. Chen is present Hsiao-Ting always denies or stays vague, and Mrs. Chen jumps in with "She doesn't have a boyfriend"; only when Mrs. Chen is gone and the clerk is respectful or explains confidentiality does Hsiao-Ting haltingly tell the truth. Asked about her period, she first says "It just came last week"; only when pressed on the amount and color does she describe the spotting, and only when asked about the last normal one does she give real_lmp.
[ACTIONS] act can only be none, mother_leave, mother_return or toilet. Use mother_leave when the clerk politely asks Mrs. Chen to wait outside for a while (she really walks out; if no reason is given or the tone is rude, she may ask why first and won't necessarily go); use mother_return when she is asked to come back; use toilet when the clerk agrees to let Hsiao-Ting go to the restroom by herself.
[STATE] ${clock()}; Mrs. Chen ${here ? "is at the bedside" : "is out in the waiting area chairs"}; Hsiao-Ting: ${condition()}. Revealed so far: ${[...S.revealed].join(",") || "none"}`,
        ),
        extra: `"revealed":[],"act":"none"`,
        after(r) {
          for (const id of strList(r.revealed)) if (isFact(id)) S.revealed.add(id);
          if (r.act === "mother_leave") {
            S.motherAwayEver = true;
            momLeave();
          } else if (r.act === "mother_return") momBack(tr("陳媽媽回到床邊。", "Mrs. Chen comes back to the bedside."));
          else if (r.act === "toilet") toilet();
        },
      });
    },
  },
  mom: {
    title: tr("陳媽媽", "Mrs. Chen"),
    sub: tr("在外面的候診椅", "Out in the waiting area"),
    pics: ["mother"],
    ch: "mom",
    talk: true,
    ph: tr("對陳媽媽說…", "Say something to Mrs. Chen…"),
    async onTalk(text) {
      await chat(text, {
        ch: "mom",
        think: "mother",
        speakers: { mother: tr("陳媽媽", "Mrs. Chen") },
        body: tr(
          `你在醫學教育遊戲中扮演「陳媽媽」，52 歲，女兒曉婷（19 歲）在急診 7 床。她剛被醫學生請到外面等，坐在候診椅上，很焦慮，一直想知道是不是盲腸炎、要不要開刀。她完全不知道女兒有男朋友。
【行動】act：clerk 請她回床邊時用 mother_return，否則 none。`,
          `In a medical education game you play "Mrs. Chen", 52, whose daughter Hsiao-Ting (19) is in ER bed 7. The medical student has just asked her to wait outside; she is sitting in the waiting area chairs, very anxious, and keeps wanting to know whether it's appendicitis and whether she needs surgery. She has no idea her daughter has a boyfriend.
[ACTIONS] act: mother_return when the clerk asks her to come back to the bedside, otherwise none.`,
        ),
        extra: `"act":"none"`,
        after(r) {
          if (r.act === "mother_return") {
            momBack(tr("陳媽媽回到床邊。", "Mrs. Chen comes back to the bedside."));
            closeDialog();
          }
        },
      });
    },
  },
  nurse: {
    title: tr("阿芬", "Fen"),
    sub: tr("急診護理師", "ER nurse"),
    pics: ["afen"],
    ch: "nurse",
    talk: true,
    ph: tr("對阿芬說，也可以口頭開醫囑", "Talk to Fen, or give a verbal order"),
    choices: nurseChoices,
    async onTalk(text) {
      const last = S.vitalsSeen.at(-1);
      await chat(text, {
        ch: "nurse",
        think: "afen",
        speakers: { afen: tr("阿芬", "Fen") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演急診資深護理師「阿芬」，十二年資歷，很忙、講話直接、偶爾吐槽但心腸好，台式中英夾雜。照醫師口頭醫囑做事，不替 clerk 做診斷；不妥的醫囑會說「醫師你確定嗎？」。
【隱藏真相，不能說出】子宮外孕破裂、腹內出血。
【現況】${clock()}；病人位置：${PT_LOC_NAME[S.ptLoc]}${S.collapsed ? "（剛昏倒）" : ""}；最近的生命徵象：${last ? vtxt(last.v) : "分診 108/70、96"}
clerk 目前知道與做過的：
${known()}
【可開立的醫囑 id】${Object.entries(ORD)
            .map(([k, v]) => `${k} ${v[0]}`)
            .join("；")}
clerk 明確要求的醫囑，把 id 放進 orders。被問到結果只能說已出來的。`,
          `In a medical education game you play "Fen", a senior ER nurse with twelve years on the job: very busy, blunt, cracks the odd joke at your expense but has a good heart, and talks in quick hospital shorthand. She carries out the doctors' verbal orders and does not make the diagnosis for the clerk; for an order that seems wrong she says "Doctor, are you sure?".
[HIDDEN TRUTH, never say it] Ruptured ectopic pregnancy, intra-abdominal bleeding.
[CURRENT STATUS] ${clock()}; patient location: ${PT_LOC_NAME[S.ptLoc]}${S.collapsed ? " (just collapsed)" : ""}; latest vitals: ${last ? vtxt(last.v) : "triage 108/70, HR 96"}
What the clerk currently knows and has done:
${known()}
[ORDER IDS YOU CAN PLACE] ${Object.entries(ORD)
            .map(([k, v]) => `${k} ${v[0]}`)
            .join("; ")}
For orders the clerk explicitly asks for, put the ids in orders. When asked about results, only report the ones that are already back.`,
        ),
        extra: `"orders":[]`,
        after(r) {
          for (const id of strList(r.orders)) if (isOrder(id)) order(id);
        },
      });
    },
  },
  er: {
    title: tr("阿哲", "Che"),
    sub: tr("急診住院醫師", "ER resident"),
    pics: ["er"],
    ch: "er",
    talk: true,
    ph: tr("對阿哲說…", "Say something to Che…"),
    async onTalk(text) {
      await chat(text, {
        ch: "er",
        think: "er",
        speakers: { er: tr("阿哲", "Che") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演急診第二年住院醫師「阿哲」，很忙、手上有十幾床，講話快。他照會了婦產科看 7 床林曉婷（19 歲，右下腹痛，分診 108/70、96、沒發燒），他覺得像闌尾炎，還沒驗孕（「她說不可能懷孕」）。他會叫學弟有問題直接找阿芬開單。不要說出診斷。`,
          `In a medical education game you play "Che", a second-year ER resident: very busy, a dozen-plus beds on his hands, talks fast. He consulted OB/GYN to see Lin Hsiao-Ting in bed 7 (19, right lower quadrant pain, triage 108/70, HR 96, no fever). He thinks it looks like appendicitis and hasn't done a pregnancy test ("she says there's no way she's pregnant"). He tells the clerk, his junior, to go straight to Fen to put in orders if they need anything. Do not state the diagnosis.`,
        ),
      });
    },
  },
  vs: {
    title: tr("林主任", "Dr. Lin"),
    sub: () =>
      S.vsHere ? tr("到場了", "Here") : S.trip ? tr("趕來的路上", "On the way") : tr("電話（在家）", "Phone (at home)"),
    pics: ["vs"],
    ch: "vs",
    talk: true,
    ph: tr("向林主任報告…", "Present to Dr. Lin…"),
    keepOpen: true,
    onTalk: (text) => vsTalk(text),
    onCall: (reason) => vsTalk("", reason),
  },
  phone: {
    title: tr("護理站電話", "Nurses’ station phone"),
    sub: tr("要打給誰？", "Who do you call?"),
    ch: "phone",
    choices() {
      return [
        C(tr("打給林主任", "Call Dr. Lin"), () => openDialog("vs"), S.vsHere),
        C(tr("打給 R2 小周", "Call Chou (R2)"), () => {
          me("phone", tr("（打給小周）", "(Calls Chou)"));
          post(
            "phone",
            "npc",
            "r2",
            tr(
              "超音波的話，跟阿芬開單，我做完這個就過去。",
              "For an ultrasound, put the order in with Fen. I'll come over once I finish this one.",
            ),
          );
          spend(1);
        }),
      ];
    },
  },
  lab: {
    title: tr("急診電腦", "ER computer"),
    sub: tr("檢驗結果", "Lab results"),
    ch: "lab",
    doc: doc(() => {
      const r = S.results.map((x): [string, string] => [clock(x.t), x.text]);
      const p = (Object.entries(S.orders) as [OrderId, Order][])
        .filter(([, o]) => !o.done)
        .map(([id, o]): [string, string] => [ORD[id][0], tr(`約 ${clock(o.ready)}`, `Around ${clock(o.ready)}`)]);
      return r.length || p.length ? [...r, ...p] : [[tr("檢驗", "Labs"), tr("還沒開任何檢查", "Nothing ordered yet")]];
    }),
  },
};

/* ---------- level ---------- */

export const L2: Level = {
  id: "l2",
  no: tr("二", "2"),
  short: tr("第二關　急診", "Level 2 · ER"),
  title: tr("急診：腹痛的少女", "ER: The Girl with Belly Pain"),
  place: tr("急診觀察區", "ER observation area"),
  blurb: tr(
    "19 歲右下腹痛，急診懷疑盲腸炎。",
    "A 19-year-old with right lower belly pain. The ER thinks it’s appendicitis.",
  ),
  cast: ["xiaoting_er", "mother", "afen", "er", "vs", "r2"],
  intro: tr(
    [
      "週五晚上 21:40，你是今晚跟值班的婦產科 clerk。急診照會：7 床林曉婷，19 歲，右下腹痛，懷疑盲腸炎，想請婦產科「排除一下婦科問題」。",
      "媽媽陪在床邊，很急。你走進急診，阿哲（急診 R）在護理站對你招手。",
    ],
    [
      "Friday, 21:40. You’re the OB/GYN clerk on call tonight. An ER consult: bed 7, Lin Hsiao-Ting, 19, right lower quadrant pain, suspected appendicitis. They’d like OB/GYN to “just rule out anything gynecologic.”",
      "Her mother is at the bedside, frantic. You walk into the ER, and Che (the ER resident) waves you over from the nurses’ station.",
    ],
  ),
  start: 21 * 60 + 40,
  maxT: 95,
  night: true,
  idleSec: 12,
  map: [
    "####################",
    "#aaaaaaaaaa#bbbbbbb#",
    "#aaaaaaaaaa#bbbbbbb#",
    "#aaaaaaaaaa#bbbbbbb#",
    "#aaaaaaaaaa#bbbbbbb#",
    "#aaaaaaaaaaabbbbbbb#",
    "#aaaaaaaaaaabbbbbbb#",
    "#aaaaaaaaaa#bbbbbbb#",
    "#aaaaaaaaaa#bbbbbbb#",
    "#aaaaaaaaaa#bbbbbbb#",
    "#aaaaaaaaaa#bbbbbbb#",
    "####################",
  ],
  zones: {
    a: ["#dfe8ee", "#c9d6df", "#d4dfe7"],
    b: ["#e9e1cf", "#d8cdb5", "#e0d6c0"],
  },
  windows: [],
  exits: [DOOR, [19, 8], [0, 8]],
  chName: {
    bed: tr("7 床", "Bed 7"),
    nurse: tr("阿芬", "Fen"),
    er: tr("阿哲", "Che"),
    vs: tr("林主任", "Dr. Lin"),
    phone: tr("電話", "Phone"),
    mom: tr("陳媽媽", "Mrs. Chen"),
    lab: tr("急診電腦", "ER computer"),
    sys: tr("急診", "ER"),
  },
  objects: [
    { id: "mon", type: "monitorStand", x: 2, y: 2, label: tr("監視器", "Monitor") },
    { id: "bed", type: "l2bed", x: 3, y: 2, h: 2, label: tr("7 床：曉婷", "Bed 7: Hsiao-Ting") },
    {
      id: "bed8",
      type: "bed",
      x: 7,
      y: 2,
      h: 2,
      pt: true,
      hair: "#3a3a44",
      skin: "#e8b090",
      sheet: "#e8e8f0",
      label: tr("8 床", "Bed 8"),
    },
    { id: "us", type: "us", x: 9, y: 6, label: tr("超音波機", "Ultrasound") },
    { id: "wcs", type: "wcdoor", x: 0, y: 8, label: tr("廁所", "Restroom") },
    { id: "bed9", type: "bed", x: 3, y: 8, h: 2, label: tr("空床", "Empty bed") },
    { id: "counter", type: "counter", x: 13, y: 2, w: 4 },
    { id: "phone", type: "phone", x: 13, y: 2, label: tr("電話", "Phone") },
    { id: "lab", type: "computer", x: 16, y: 2, label: tr("急診電腦：檢驗結果", "ER computer: lab results") },
    { id: "chairs", type: "chairs", x: 13, y: 9, w: 3 },
    { id: "water", type: "water", x: 18, y: 9 },
  ],
  labels: {
    bed: tr("7 床：曉婷、陳媽媽", "Bed 7: Hsiao-Ting, Mrs. Chen"),
    nurse: tr("阿芬", "Fen"),
    er: tr("阿哲（急診 R）", "Che (ER resident)"),
    mom: tr("陳媽媽", "Mrs. Chen"),
    vs: tr("林主任", "Dr. Lin"),
    r2tile: tr("小周", "Chou"),
  },
  help: {
    mon: tr("生命徵象（會打開大螢幕）", "Vitals (opens full screen)"),
    bed: tr("問診、身體檢查（會打開特寫）", "History, physical exam (opens a close-up)"),
    bed8: tr("8 床的伯伯", "The old man in bed 8"),
    us: tr("超音波機（R2 才能做）", "Ultrasound (only the R2 can scan)"),
    wcs: tr("廁所", "Restroom"),
    bed9: tr("空床", "Empty bed"),
    phone: tr("打給林主任、R2 小周", "Call Dr. Lin, or Chou (R2)"),
    lab: tr("檢驗結果", "Lab results"),
    nurse: tr("開醫囑、口頭醫囑", "Orders, verbal orders"),
    er: tr("照會你的急診 R", "The ER resident who consulted you"),
    mom: tr("陳媽媽（在外面等）", "Mrs. Chen (waiting outside)"),
  },
  helpRules: tr(
    [
      "病人在媽媽面前會隱瞞一些事。",
      "時間會一直走，病人的狀況會變差。",
      "打給林主任時，你講得多急、多清楚，他就來得多快。左下角會顯示他到哪了。",
    ],
    [
      "The patient keeps some things back in front of her mother.",
      "The clock keeps running, and the patient keeps getting worse.",
      "When you call Dr. Lin, the more urgent and clear you are, the faster he comes. The bottom-left corner shows where he is.",
    ],
  ),
  player: [12, 6],
  ents: () => [
    mkEnt({ k: "mother", id: "mother", x: 4, y: 3, act: "bed", face: "left" }),
    mkEnt({ k: "afen", id: "afen", x: 14, y: 4, act: "nurse" }),
    mkEnt({ k: "er", id: "er", x: 17, y: 4, act: "er", face: "up" }),
  ],
  init: (): Vars => ({
    revealed: new Set(),
    orders: {},
    results: [],
    vitalsSeen: [],
    motherAway: null,
    motherAwayEver: false,
    ptLoc: "bed",
    collapsed: false,
    collapseAt: null,
    fluidAt: null,
    hcgKnownAt: null,
    ctWarned: false,
    ctLeave: null,
    vsTalked: false,
    vsAccepted: false,
    vsArriveAt: null,
    vsHere: false,
    trip: null,
    bedAnim: null,
    bedGone: false,
    toiletAt: null,
    r2State: "none",
    callAt: null,
    endAt: null,
  }),
  dialogs,
  zooms: { bed: bedZoom },
  quick: {
    mon: openMon,
    bed8: () =>
      note(
        tr("8 床的伯伯喝醉了，在打呼。一股酒味。", "The old man in bed 8 is drunk and snoring. He reeks of alcohol."),
      ),
    wcs: () =>
      note(S.ptLoc === "wc" ? tr("廁所裡有人。", "Someone’s in the restroom.") : tr("廁所。", "The restroom.")),
    bed9: () => note(tr("空床。", "Empty bed.")),
    us: () =>
      note(
        tr(
          "超音波機。要照的話，跟阿芬說請 R2 小周來。",
          "The ultrasound machine. For a scan, ask Fen to get Chou, the R2.",
        ),
      ),
    r2tile: () =>
      toast({ ch: "bed", kind: "npc", who: "r2", text: tr("我在照，你等一下。", "I'm scanning. Hang on."), t: S.t }),
  },

  onStart: () => eventBanner(tr("急診照會", "ER consult"), tr("7 床，19 歲，右下腹痛", "Bed 7, 19, RLQ pain")),
  tick(t) {
    if (t === 1)
      ask(
        "er",
        "er",
        tr(
          "學弟！7 床林曉婷，19 歲，下午開始右下腹痛。分診 BP 108/70，HR 96，沒發燒。我們覺得像 appendicitis，想請婦產科先排除一下。",
          "Hey, over here! Bed 7, Lin Hsiao-Ting, 19, right lower quadrant pain since this afternoon. Triage BP 108/70, HR 96, afebrile. We think it's appendicitis, just want OB/GYN to rule things out first.",
        ),
        {
          id: "consult",
          text: tr("阿哲在等你接照會", "Che is waiting to hand you the consult"),
          dlg: "er",
          clearOn: "open",
          icon: tr("！", "!"),
        },
      );
    if (t === 2)
      post(
        "bed",
        "npc",
        "mother",
        tr(
          "醫生你終於來了！她從下午痛到現在，是不是盲腸炎？要不要開刀？",
          "Doctor, finally! She's been in pain since this afternoon. Is it appendicitis? Does she need surgery?",
        ),
      );
    for (const [id, o] of Object.entries(S.orders) as [OrderId, Order][]) if (!o.done && t >= o.ready) deliver(id);
    const tvs = S.orders.tvs;
    if (S.r2State === "coming" && tvs && t === tvs.ready - 4) {
      S.r2State = "here";
      spawnAt("r2", "r2tile", DOOR, [2, 4]);
      post("bed", "npc", "r2", tr("我來照超音波。", "I'm here for the ultrasound."));
    }
    if (S.ctLeave === t && S.ptLoc === "bed" && !S.collapsed) {
      S.ptLoc = "ct";
      post("bed", "narr", null, tr("傳送人員把曉婷推去做 CT。", "Transport wheels Hsiao-Ting off to CT."));
    }
    if (S.ptLoc === "ct" && S.orders.ct?.done && !S.collapsed) {
      S.ptLoc = "bed";
      post(
        "bed",
        "narr",
        null,
        tr("曉婷做完 CT 被推回來，臉比剛才更白。", "Hsiao-Ting is wheeled back from CT, paler than before."),
      );
    }
    if (S.ptLoc === "wc" && S.toiletAt !== null && t === S.toiletAt + 4 && !S.collapsed) {
      S.ptLoc = "bed";
      post(
        "bed",
        "narr",
        null,
        tr("曉婷從廁所慢慢走回來，扶著牆。", "Hsiao-Ting shuffles back from the restroom, one hand on the wall."),
      );
    }
    if (S.motherAway !== null && t === S.motherAway + 15 && !S.collapsed)
      momBack(tr("陳媽媽拿著一杯熱水回來了。", "Mrs. Chen comes back with a cup of hot water."));
    if (t === 12 && !S.vsTalked)
      post("vs", "npc", "vs", tr("（LINE）7 床看了嗎？看完打給我。", "(LINE) Seen bed 7 yet? Call me when you have."));
    if (t === 28 && !S.collapsed && !S.vsAccepted) {
      const v = vitals();
      S.vitalsSeen.push({ t, v });
      post(
        "nurse",
        "alert",
        "afen",
        tr(
          `醫師，7 床再量一次，BP ${v.sbp}/${v.dbp}，HR ${v.hr}。她說坐起來就很暈。`,
          `Doctor, rechecked bed 7: BP ${v.sbp}/${v.dbp}, HR ${v.hr}. She says she gets really dizzy when she sits up.`,
        ),
      );
    }
    if (t === 38 && !S.collapsed && S.ptLoc === "bed" && !S.vsAccepted)
      ask("bed", "xiaoting_er", tr("醫生……我想去廁所。", "Doctor… I need to use the restroom."), {
        id: "toilet",
        text: tr("曉婷想去廁所", "Hsiao-Ting needs the restroom"),
        dlg: "bed",
      });
    if (!S.collapsed && !S.vsHere && t >= threshold()) collapse();
    if (S.callAt === t && !S.vsAccepted) {
      S.callAt = null;
      incomingCall("collapse", "vs");
    }
    if (S.collapsed && !S.vsAccepted && S.collapseAt !== null && t === S.collapseAt + 6) {
      S.vsAccepted = true;
      S.vsArriveAt = t + 5;
      startVsTrip("high", 5);
      post("nurse", "npc", "afen", tr("主任說他直接過來了。", "The attending says he's coming straight in."));
    }
    if (S.vsArriveAt === t) {
      S.vsHere = true;
      pipSet(null);
      spawnAt("vs", "bed", DOOR, [5, 4]);
      eventBanner(tr("林主任到場", "Dr. Lin arrives"));
      S.endAt = t + 2;
    }
    if (S.endAt === t) {
      if (S.collapsed) endB();
      else endA();
    }
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          who: "er",
          text: tr("學弟，大夜班的婦產科來了，你交班吧。", "Hey, the overnight OB/GYN team is here. Go sign out."),
        },
      ],
      tr("時間到，交班。", "Time's up. Sign-out."),
    ),

  hud() {
    const last = S.vitalsSeen.at(-1);
    return [
      [
        last ? tr(`${clock(last.t)} 生命徵象`, `Vitals ${clock(last.t)}`) : tr("生命徵象", "Vitals"),
        last
          ? tr(`${last.v.sbp}/${last.v.dbp}　HR ${last.v.hr}`, `${last.v.sbp}/${last.v.dbp} · HR ${last.v.hr}`)
          : tr("分診 108/70　HR 96", "Triage 108/70 · HR 96"),
      ],
      [
        tr("驗孕", "Preg test"),
        S.hcgKnownAt !== null
          ? tr("陽性", "Positive")
          : S.orders.upt || S.orders.bhcg
            ? tr("等待中", "Pending")
            : tr("還沒驗", "Not done"),
      ],
    ];
  },
  urgent: () => S.t >= 28 || S.collapsed,
  drawBelow(c, now) {
    const px = 2 * T;
    const py = 2 * T;
    c.fillStyle = "#8a90a2";
    c.fillRect(px + 7, py + 6, 2, 10);
    c.fillStyle = "#3a3f52";
    c.fillRect(px + 1, py, 14, 9);
    c.fillStyle = "#0f1f1a";
    c.fillRect(px + 2, py + 1, 12, 7);
    const fast = vitals().hr > 112;
    c.fillStyle = "#5be38a";
    for (let i = 0; i < 12; i++) {
      const ph = (i + Math.floor(now / (fast ? 60 : 110))) % 12;
      c.fillRect(px + 2 + i, ph === 3 ? py + 2 : ph === 4 ? py + 6 : py + 4, 1, 1);
    }
    if (!S.bedGone) {
      const p = bedAnimPos(S.bedAnim, now, [3, 2]);
      if (!p) S.bedGone = true;
      else
        DRAW.bed?.(
          c,
          Math.round(p[0] * T),
          Math.round(p[1] * T),
          S.ptLoc === "bed"
            ? { pt: true, hair: "#1d1a26", skin: S.collapsed || S.t > 34 ? "#ece4e0" : "#f6dcc8", sheet: "#e8e8f0" }
            : {},
        );
    }
    if (!S.collapsed && S.ptLoc === "bed" && S.t > 30 && Math.floor(now / 600) % 2)
      R(c, "#9fd0ef", 3 * T + 11, 2 * T + 4, 2, 2);
  },

  graderCase: tr(
    `病例：19 歲女性右下腹痛，急診懷疑闌尾炎照會婦產科。真相是右側子宮外孕破裂、腹內出血，約 45 分鐘後會昏倒（先輸液會延後）。母親在場時，病人會隱瞞性生活史；clerk 必須自己想到請家屬暫時離開。沒驗孕就開 CT，護理師會提醒一次。病人中途想去廁所，放她自己去會在廁所昏倒。主治趕來的速度取決於 clerk 電話裡傳達的急迫性。`,
    `Case: a 19-year-old woman with right lower quadrant pain; the ER suspects appendicitis and consults OB/GYN. The truth is a ruptured right ectopic pregnancy with intra-abdominal bleeding; she collapses after about 45 minutes (giving fluids early delays this). While her mother is present, the patient hides her sexual history; the clerk has to think, unprompted, of asking the family to step out for a while. If CT is ordered without a pregnancy test, the nurse warns once. Partway through, the patient wants to go to the restroom; letting her go by herself means she collapses there. How fast the attending arrives depends on the urgency the clerk conveys on the phone.`,
  ),
  rubric: tr(
    [
      "育齡女性腹痛先驗孕",
      "隱私與性生活史：請母親離開、尊重地問",
      "月經史：分辨異常出血與前一次正常月經",
      "危險徵象：生命徵象趨勢、頭暈、肩痛；病人想下床時的判斷",
      "初步穩定：大號 IV、輸液、備血、禁食",
      "影像：陰道超音波；避免懷孕未排除就做 CT",
      "向主治報告：時機、內容與急迫性",
    ],
    [
      "Pregnancy test first for abdominal pain in a woman of reproductive age",
      "Privacy and sexual history: asking the mother to step out, asking respectfully",
      "Menstrual history: telling abnormal bleeding apart from the last normal period",
      "Red flags: vital sign trend, dizziness, shoulder pain; judgment when the patient wants to get out of bed",
      "Initial stabilization: large-bore IVs, fluids, blood prepared, NPO",
      "Imaging: transvaginal ultrasound; avoiding CT before pregnancy has been ruled out",
      "Presenting to the attending: timing, content and urgency",
    ],
  ),
  facts: () => [
    tr(
      `驗孕結果：${S.hcgKnownAt !== null ? clock(S.hcgKnownAt) : "沒驗"}`,
      `Pregnancy test result: ${S.hcgKnownAt !== null ? clock(S.hcgKnownAt) : "not done"}`,
    ),
    tr(
      `請媽媽離開：${S.motherAwayEver ? "有" : "沒有"}`,
      `Asked the mother to step out: ${S.motherAwayEver ? "yes" : "no"}`,
    ),
    tr(
      `問出病史：${S.revealed.size}/${Object.keys(FACTS).length}`,
      `History items obtained: ${S.revealed.size}/${Object.keys(FACTS).length}`,
    ),
    tr(
      `輸液：${S.fluidAt !== null ? clock(S.fluidAt) : "沒有"}`,
      `Fluids: ${S.fluidAt !== null ? clock(S.fluidAt) : "none"}`,
    ),
    tr(
      `昏倒：${S.collapsed ? `是（${clock(S.collapseAt ?? S.t)}）` : "否"}`,
      `Collapsed: ${S.collapsed ? `yes (${clock(S.collapseAt ?? S.t)})` : "no"}`,
    ),
    tr(
      `主任趕來：${S.trip ? { high: "很急", medium: "普通", low: "不急" }[S.trip.urg] : "沒有叫"}`,
      `Attending coming in: ${S.trip ? { high: "in a hurry", medium: "at a normal pace", low: "unhurried" }[S.trip.urg] : "never called"}`,
    ),
  ],
  known,
};
